import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { DatePicker, Drawer, message } from "antd";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import dayjs, { Dayjs } from "dayjs";
import { getEmployees } from "../../../core/data/redux/employee/employeeAction";
import { fetchAllAssetCategories } from "../../../core/data/redux/asset/assetCategoryActions";
import { createAsset, deleteAsset, fetchAllAssets, fetchAssetLog, fetchAssetSummary, updateAsset } from "../../../core/data/redux/asset/asset/assetActions";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import { exportToCSV, exportToExcel, exportToPDF } from "../../../core/common/GlobalFunctions/exportTableData";
import DeleteModal1 from "../../../core/modals/deleteModal1";
import AssetAllocationModel from "./assetAllocationModel";
import { convertFileToBase64 } from "../../../core/common/GlobalFunctions/filetobase64";
import AssetLogDrawer from "./AssetLogDrawer";
import { formatDate } from "../../../core/utils/configFormatters";
import { useAppConfig } from "../../../customhooks/useAppConfig";
import { decryptData } from "../../../core/utils/encryptUtils";
import { filterByStatus } from "../../../core/utils/filterActiveData";
import { fetchAllClients } from "../../../core/data/redux/projects/client/clientAction";

type AssetDetail = {
  AssetTagNumber: string;
  SerialNumber: string;
  WarrantyDate: Dayjs;
  asset_details_id: string;
};

const Assets = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [quantity, setQuantity] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [errors, setErrors] = useState<any>({});
  const [selectedSort, setSelectedSort] = useState<any>("");
  const [tableKey, setTableKey] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [assetAllocationModal, setAssetAllocationModal] =
    useState<boolean>(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] =
    useState<boolean>(false);
  const [modalType, setModalType] = useState<"edit" | "create" | "">("");
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const { user } = useSelector((state: any) => state.auth);
  const { employees } = useSelector((state: any) => state.employee);
  const { assetCategories } = useSelector(
    (state: any) => state.asset.assetCategory
  );
  const activeAssetCategory = filterByStatus(assetCategories, "active", "status");
  const { assetSummary, assets, assetLogs, loading } = useSelector(
    (state: any) => state.asset.newAsset
  );
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const { clients } = useSelector((state: any) => state.project.clients);
  const employeeData = employees ? employees : [];
  const activeAssetOwner = filterByStatus(employeeData, "active", "employee_status");
  const [assetDetailsForm, setAssetDetailsForm] = useState({
    assetDetails: [
      {
        AssetTagNumber: "",
        SerialNumber: "",
        WarrantyDate: dayjs(),
        asset_details_id: "",
      },
    ],
  });

  const filteredDataVendor = clients?.filter((client: any) => client.client_or_vendor === 1) || [];
  const [assetFormData, setAssetFormData] = useState<any>({
    OwnerEmpId: "",
    status: "active",
    PurchaseDate: dayjs(),
    Description: "",
    PurchasePrice: "",
    PurchaseOrderNumber: "",
    PurchaseOrderBill: "",
    SupportCenter: "",
    Manufacturer: "",
    Model: "",
    BrandName: "",
    ProductionYear: "",
    AssetName: "",
    asset_category_id: "",
    PurchaseFrom: "",
    file: null,
  });
  const resetForm = () => {
    setQuantity(1);
    setAssetFormData({
      OwnerEmpId: "",
      status: "active",
      PurchaseDate: dayjs(),
      Description: "",
      PurchasePrice: "",
      PurchaseOrderNumber: "",
      PurchaseOrderBill: "",
      SupportCenter: "",
      Manufacturer: "",
      Model: "",
      BrandName: "",
      ProductionYear: "",
      AssetName: "",
      asset_category_id: "",
      PurchaseFrom: "",
      file: null,
    });
    setAssetDetailsForm({
      assetDetails: [
        {
          AssetTagNumber: "",
          SerialNumber: "",
          WarrantyDate: dayjs(),
          asset_details_id: "",
        },
      ],
    });
    setErrors({});
  };

  const handleQuantityChange = (e: any) => {
    const newQuantity = Number(e.target.value);
    if (isNaN(newQuantity) || newQuantity < 1) {
      message.warning("Quantity must be at least 1.");
      return;
    }
    if (newQuantity > 50) {
      message.warning("Maximum allowed quantity is 50.");
      return;
    }
    setQuantity(newQuantity);
    setAssetDetailsForm((prevState) => {
      const updatedAssetDetails = [...prevState.assetDetails];
      if (newQuantity > updatedAssetDetails.length) {
        for (let i = updatedAssetDetails.length; i < newQuantity; i++) {
          updatedAssetDetails.push({
            AssetTagNumber: "",
            SerialNumber: "",
            WarrantyDate: dayjs(),
            asset_details_id: "",
          });
        }
      } else if (newQuantity < updatedAssetDetails.length) {
        updatedAssetDetails.splice(newQuantity);
      }
      return { ...prevState, assetDetails: updatedAssetDetails };
    });
  };
  const handleAssetDetailsChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    field: keyof AssetDetail
  ) => {
    const updatedAssetDetails = [...assetDetailsForm.assetDetails];

    if (field === "WarrantyDate") {
      updatedAssetDetails[index][field] = dayjs(e.target.value);
    } else {
      updatedAssetDetails[index][field] = e.target.value;
    }

    setAssetDetailsForm({
      ...assetDetailsForm,
      assetDetails: updatedAssetDetails,
    });
  };

  const validateForm = () => {
    const newErrors: any = {};
    if (!assetFormData.AssetName) {
      newErrors.AssetName = "Asset Name is required.";
    }
    if (!assetFormData.asset_category_id) {
      newErrors.asset_category_id = "Asset Category is required.";
    }

    if (
      !assetFormData.PurchasePrice ||
      isNaN(Number(assetFormData.PurchasePrice))
    ) {
      newErrors.PurchasePrice = "Purchase Price must be a valid number.";
    }
    if (!assetFormData.OwnerEmpId) {
      newErrors.OwnerEmpId = "Asset Owner is required.";
    }
    if (!assetFormData.ProductionYear) {
      newErrors.ProductionYear = "Production year is required.";
    }
    if (!assetFormData.PurchaseDate) {
      newErrors.PurchaseDate = "Purchase Date is required.";
    }
    if (!assetFormData.SupportCenter) {
      newErrors.SupportCenter = "Support Center is required.";
    }
    if (!assetFormData.Manufacturer) {
      newErrors.Manufacturer = "Manufacturer is required.";
    }
    if (!assetFormData.Model) {
      newErrors.Model = "Model is required.";
    }
    if (!assetFormData.BrandName) {
      newErrors.BrandName = "Brand Name is required.";
    }
    if (!assetFormData.PurchaseFrom) {
      newErrors.PurchaseFrom = "Purchase From is required.";
    }
    if (!assetFormData.status) {
      newErrors.status = "Status is required.";
    }
    if (!assetFormData.file) {
      newErrors.file = "Asset image is required.";
    }


    // Validate asset details
    const assetTagNumbers: Set<string> = new Set();
    const serialNumbers: Set<string> = new Set();
    const assetDetailsErrors = assetDetailsForm.assetDetails.map(
      (assetDetail, index) => {
        const errorsForAsset: {
          AssetTagNumber?: string;
          SerialNumber?: string;
          WarrantyDate?: string;
        } = {};

        if (!assetDetail.AssetTagNumber) {
          errorsForAsset["AssetTagNumber"] = `Asset Tag Number for asset ${index + 1
            } is required.`;
        }

        if (!assetDetail.SerialNumber) {
          errorsForAsset["SerialNumber"] = `Serial Number for asset ${index + 1
            } is required.`;
        }

        if (assetTagNumbers.has(assetDetail.AssetTagNumber)) {
          errorsForAsset["AssetTagNumber"] = `Asset Tag Number for asset ${index + 1
            } is duplicated.`;
        } else {
          assetTagNumbers.add(assetDetail.AssetTagNumber);
        }

        if (serialNumbers.has(assetDetail.SerialNumber)) {
          errorsForAsset["SerialNumber"] = `Serial Number for asset ${index + 1
            } is duplicated.`;
        } else {
          serialNumbers.add(assetDetail.SerialNumber);
        }

        if (
          !assetDetail.WarrantyDate ||
          !dayjs(assetDetail.WarrantyDate).isValid()
        ) {
          errorsForAsset["WarrantyDate"] = `Warranty Date for asset ${index + 1
            } is required and must be valid.`;
        }

        return errorsForAsset;
      }
    );
    assetDetailsErrors.forEach((errorsForAsset, index) => {
      if (Object.keys(errorsForAsset).length > 0) {
        newErrors[`assetDetail_${index}`] = errorsForAsset;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (employeeData.length === 0) {
      dispatch(getEmployees());
    }
    if (!assetCategories.length) {
      dispatch(fetchAllAssetCategories());
    }
    if (!assets.length) {
      dispatch(fetchAllAssets());
    }
    if (!assetSummary?.total_assets) {
      dispatch(fetchAssetSummary());
    }
    if (!assetLogs.length) {
      dispatch(fetchAssetLog());
    }
    dispatch(fetchAllClients())
  }, [assetCategories.length, assetLogs.length, assetSummary?.total_assets, assets.length, dispatch, employeeData.length,]);

  const showModal = (type: "edit" | "create") => {
    setModalType(type);
    setIsModalVisible(true);
  };
  const handleCancel = () => {
    setIsModalVisible(false);
    setModalType("");
    resetForm();
  };

  const handleEditAssetCategory = (record: any): void => {
    setSelectedRowData(record);
    setAssetFormData({
      OwnerEmpId: record.OwnerEmpId,
      status: record.status || "active",
      PurchaseDate: record.PurchaseDate ? dayjs(record.PurchaseDate) : dayjs(),
      Description: record.Description,
      PurchasePrice: record.PurchasePrice,
      PurchaseOrderNumber: record.PurchaseOrderNumber,
      PurchaseOrderBill: record.PurchaseOrderBill,
      SupportCenter: record.SupportCenter,
      Manufacturer: record.Manufacturer,
      Model: record.Model,
      BrandName: record.BrandName,
      ProductionYear: record.ProductionYear,
      AssetName: record.AssetName,
      asset_category_id: record.asset_category_id,
      PurchaseFrom: record.PurchaseFrom,
      // file: record.Images ? { name: record.Images.split("/").pop() } : null,
    });
    if (record.Images) {
      convertFileToBase64(record.Images)
        .then((base64: any) => {
          const fileName = record.Images.split("/").pop() || "unknown file";
          setAssetFormData((prev: any) => ({
            ...prev,
            file: {
              name: fileName,
              data: base64,
            },
          }));
        })
        .catch((error: any) => {
          console.error("Error converting file to base64:", error);
        });
    }

    // Ensure that assetDetails are updated properly
    if (record.assetDetails && record.assetDetails.length > 0) {
      setAssetDetailsForm((prevState) => {
        // Create an updated list of asset details by merging record.assetDetails
        const updatedAssetDetails = record.assetDetails.map(
          (recordDetail: any, index: any) => {
            const prevDetail = prevState.assetDetails[index] || {}; // Fallback to empty object if out of index

            const warrantyDate = recordDetail.WarrantyDate;
            const validWarrantyDate = warrantyDate
              ? dayjs(warrantyDate)
              : dayjs();

            // Merge recordDetail with prevDetail, prioritize recordDetail for updated fields
            return {
              ...prevDetail, // Keep previous details if they exist
              ...recordDetail, // Override with the new record values
              WarrantyDate: validWarrantyDate.isValid()
                ? validWarrantyDate
                : dayjs(), // Ensure valid WarrantyDate
            };
          }
        );

        return { ...prevState, assetDetails: updatedAssetDetails };
      });
    }

    // Set quantity and handle assetDetails based on quantity
    setQuantity(record.Quantity);

    // Call handleQuantityChange with the record's quantity
    handleQuantityChange({
      target: { value: record.Quantity },
    });

    showModal("edit");
  };

  const handleInputChange = (e: any, field: string) => {
    const { name, value, files } = e.target;

    if (name === "file") {
      setAssetFormData((prev: any) => ({
        ...prev,
        file: files[0],
      }));
    } else {
      setAssetFormData((prevState: any) => ({
        ...prevState,
        [field]: value,
      }));
    }
  };
  const handleRemoveFile = () => {
    setAssetFormData((prevState: any) => ({
      ...prevState,
      file: null,
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const formData = new FormData();

    // Append regular form fields
    Object.keys(assetFormData).forEach((key) => {
      if (key !== "file") {
        formData.append(key, assetFormData[key]);
      }
    });

    // Append the file if present
    if (assetFormData.file) {
      formData.append("document", assetFormData.file);
    }
    // Append asset details as a single stringified array
    if (
      assetDetailsForm.assetDetails &&
      assetDetailsForm.assetDetails.length > 0
    ) {
      formData.append(
        "assetDetails",
        JSON.stringify(
          assetDetailsForm.assetDetails.map((asset) => ({
            asset_details_id: asset.asset_details_id || "", // Ensure empty string if missing
            AssetTagNumber: asset.AssetTagNumber,
            SerialNumber: asset.SerialNumber,
            WarrantyDate: asset.WarrantyDate.format("YYYY-MM-DD"),
            CreatedBy: user.UserId,
          }))
        )
      );
    }

    if (modalType === "create") {
      formData.append("CreatedBy", user.UserId);
    }

    if (modalType === "edit") {
      formData.append("ModifiedBy", user.UserId);
    }
    formData.append("Quantity", String(quantity));

    try {
      let result;
      if (modalType === "create") {
        result = await dispatch(createAsset(formData)).unwrap();
      } else if (modalType === "edit") {
        if (selectedRowData) {
          result = await dispatch(
            updateAsset({ assetId: selectedRowData.assetId, formData })
          ).unwrap();
        }
      }

      if (result) {
        message.success(result.message);
        await dispatch(fetchAllAssets());
        await dispatch(fetchAssetSummary());
        await dispatch(fetchAssetLog());
        setTableKey((prevKey) => prevKey + 1);
        resetForm();
      } else {
        alert(result?.payload.error);
      }

      handleCancel();
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    }
  };
  const handleDeleteAsset = async (assetId: number) => {
    try {
      const result = await dispatch(deleteAsset(assetId)).unwrap();
      if (result) {
        message.success(result.message);
        await dispatch(fetchAllAssets());
        setTableKey((prevKey) => prevKey + 1);
        setIsDeleteModalVisible(false);
      } else {
        message.warning(result.message);
      }
    } catch (error: any) {
      message.warning(error.message);
    }
  };

  const handleShowLogs = (assetId: number) => {
    setSelectedAssetId(assetId);
    dispatch(fetchAssetLog());
    setIsDrawerVisible(true);
  };
  const handleCloseDrawer = () => {
    setIsDrawerVisible(false);
    setSelectedAssetId(null);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = assets ? assets : [];

  const exportAssetData = data.map((item: any) => ({
    CreatedAt: item.CreatedAt ? new Date(item.CreatedAt).toLocaleString("en-IN") : "N/A",
    Description: item.Description,
    PurchaseDate: item.PurchaseDate ? new Date(item.PurchaseDate).toLocaleDateString("en-IN") : "N/A",
    PurchasePrice: item.PurchasePrice,
    PurchaseOrderNumber: item.PurchaseOrderNumber,
    PurchaseOrderBill: item.PurchaseOrderBill,
    SupportCenter: item.SupportCenter,
    Manufacturer: item.Manufacturer,
    Model: item.Model,
    ProductionYear: item.ProductionYear,
    AssetName: item.AssetName,
    PurchaseFrom: item.PurchaseFrom,
    status: item.status,
    BrandName: item.BrandName,
    Quantity: item.Quantity,
    availableAssetCount: item.availableAssetCount,
  }));

  // const imagePreview = useMemo(() => {
  //   if (!assetFormData.file) return "";
  //   if (assetFormData.file instanceof File) return URL.createObjectURL(assetFormData.file);
  //   if (typeof assetFormData.file === "string") return assetFormData.file;
  //   if (assetFormData.file.data) return assetFormData.file.data;
  //   return "";
  // }, [assetFormData.file]);

  // useEffect(() => {
  //   return () => {
  //     if (assetFormData.file instanceof File) URL.revokeObjectURL(imagePreview);
  //   };
  // }, [imagePreview]);

  const filteredData = useMemo(() => {
    const filters =
      selectedStatus && selectedStatus !== "All"
        ? { status: selectedStatus }
        : {};

    return filterData(
      data || [],
      filters,
      "CreatedAt",
      "CreatedAt",
      selectedSort
    );
  }, [data, selectedStatus, selectedSort]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [selectedStatus, selectedSort, assets]);

  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];


  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");
  const canExport =
    permissions.includes("Export") || permissions.includes("AllowAll");

  const config = useAppConfig();
  if (!config) return null;

  const columns = [
    {
      title: "Asset Name",
      dataIndex: "AssetName",
      render: (text: String, record: any) => (
        <Link
          to={`${all_routes.assetDetails.replace(":assetId", record.assetId)}`}
          style={{
            color: "#0d6efd",
            textDecoration: "underline",
            cursor: "pointer",
          }}
          title="View Asset Details"
        >
          {text}
        </Link>
      ),
      sorter: (a: any, b: any) => a.AssetName.length - b.AssetName.length,
    },
    {
      title: "Purchase Date",
      dataIndex: "PurchaseDate",
      render: (text: string) => formatDate(text, config),
      sorter: (a: any, b: any) => a.PurchaseDate.length - b.PurchaseDate.length,
    },
    {
      title: "Manufacturer",
      dataIndex: "Manufacturer",
      sorter: (a: any, b: any) => a.Manufacturer.length - b.Manufacturer.length,
    },
    {
      title: "Quantity",
      dataIndex: "Quantity",
      sorter: (a: any, b: any) => a.Quantity - b.Quantity,
    },
    {
      title: "Available Quantity",
      dataIndex: "availableAssetCount",
      sorter: (a: any, b: any) => a.availableAssetCount - b.availableAssetCount,
    },
    {
      title: "Warranty End Date",
      dataIndex: "assetDetails", // Reference the whole array of assetDetails
      render: (assetDetails: any) => {
        // Assuming each entry in assetDetails has a WarrantyDate, take the first entry's WarrantyDate
        if (
          assetDetails &&
          assetDetails.length > 0 &&
          assetDetails[0].WarrantyDate
        ) {
          return formatDate(assetDetails[0].WarrantyDate, config); // Format and show the first asset's WarrantyDate
        }
        return "N/A"; // Return 'N/A' if there's no WarrantyDate
      },

      sorter: (a: any, b: any) => {
        const dateA = new Date(a.assetDetails?.[0]?.WarrantyDate).getTime();
        const dateB = new Date(b.assetDetails?.[0]?.WarrantyDate).getTime();

        if (isNaN(dateA) || isNaN(dateB)) {
          return 0;
        }
        return dateA - dateB;
      },
    },

    {
      title: "Status",
      dataIndex: "status",
      render: (text: string, record: any) => (
        <>
          <span
            className={`badge d-inline-flex align-items-center badge-xs ${text === "active"
              ? "badge-success"
              : text === "inactive"
                ? "badge-danger"
                : "badge-warning"
              }`}
          >
            <i className="ti ti-point-filled me-1"></i>
            {text ? text.charAt(0).toUpperCase() + text.slice(1) : ""}

          </span>
        </>
      ),
      sorter: (a: any, b: any) => a.status.length - b.status.length,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: string, record: any) => {
        const isInactive = record.status === "inactive";
        const isAllocationDisabled =
          record.availableAssetCount === 0 || isInactive;
        const isDeleteDisabled = isInactive;

        return (
          <div className="action-icon d-inline-flex">
            {/* Allocation Button (View Permission Required) */}
            <button
              style={{
                background: "none",
                border: "none",
                color: !canView || isAllocationDisabled ? "#ccc" : "#007bff",
                cursor:
                  !canView || isAllocationDisabled ? "not-allowed" : "pointer",
              }}
              className="me-2"
              onClick={() => {
                if (canView && !isAllocationDisabled) {
                  setSelectedRowData(record);
                  setAssetAllocationModal(true);
                }
              }}
              disabled={!canView || isAllocationDisabled}
              title={
                !canView
                  ? "Permission Denied"
                  : isAllocationDisabled
                    ? "No assets available"
                    : "Allocate Asset"
              }
            >
              <i className="ti ti-shield" style={{ fontSize: "1.2rem" }} />
            </button>

            {/* Edit Button */}
            <button
              style={{
                background: "none",
                border: "none",
                color: "#ffc107",
                cursor: canEdit ? "pointer" : "not-allowed",
                opacity: canEdit ? 1 : 0.5,
              }}
              className="me-2"
              onClick={() => {
                if (!canEdit) return;
                showModal("edit");
                handleEditAssetCategory(record);
              }}
              disabled={!canEdit}
              title={!canEdit ? "Permission Denied" : "Edit"}
            >
              <i className="ti ti-edit" style={{ fontSize: "1.2rem" }} />
            </button>

            {/* Delete Button */}
            <button
              style={{
                background: "none",
                border: "none",
                color: !canDelete || isDeleteDisabled ? "#ccc" : "#dc3545",
                cursor:
                  !canDelete || isDeleteDisabled ? "not-allowed" : "pointer",
              }}
              className="me-2"
              onClick={() => {
                if (canDelete && !isDeleteDisabled) {
                  setSelectedRowData(record);
                  setIsDeleteModalVisible(true);
                }
              }}
              disabled={!canDelete || isDeleteDisabled}
              title={
                !canDelete
                  ? "Permission Denied"
                  : isDeleteDisabled
                    ? "Inactive item"
                    : "Delete"
              }
            >
              <i className="ti ti-trash" style={{ fontSize: "1.2rem" }} />
            </button>

            {/* Logs Button (View Permission Required) */}
            <button
              style={{
                background: "none",
                border: "none",
                color: canView ? "#6c757d" : "#ccc",
                cursor: canView ? "pointer" : "not-allowed",
                opacity: canView ? 1 : 0.5,
              }}
              onClick={() => {
                if (canView) handleShowLogs(record.assetId);
              }}
              className="me-2"
              disabled={!canView}
              title={!canView ? "Permission Denied" : "View Logs"}
            >
              <i className="ti ti-clock" style={{ fontSize: "1.2rem" }} />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Assets</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  {/* <li className="breadcrumb-item">Administration</li> */}
                  <li className="breadcrumb-item active" aria-current="page">
                    Assets
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              {canExport && (
                <div className="me-2 mb-2">
                  <div className="dropdown">
                    <Link
                      to="#"
                      className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                      data-bs-toggle="dropdown"
                    >
                      <i className="ti ti-file-export me-1" />
                      Export
                    </Link>
                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                      <li>
                        <button
                          onClick={() => exportToCSV(exportAssetData, "asset_data")}
                          className="dropdown-item rounded-1"
                        >
                          <i className="ti ti-file-type-pdf me-1" />
                          Export as CSV
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => exportToExcel(exportAssetData, "asset_data")}
                          className="dropdown-item rounded-1"
                        >
                          <i className="ti ti-file-type-xls me-1" />
                          Export as Excel{" "}
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => exportToPDF(exportAssetData, "asset_data")}
                          className="dropdown-item rounded-1"
                        >
                          <i className="ti ti-file-type-xls me-1" />
                          Export as PDF{" "}
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
              <div className="mb-2">
                {canAdd && (
                  <button
                    onClick={() => showModal("create")}
                    className="btn btn-primary d-flex align-items-center"
                  >
                    <i className="ti ti-circle-plus me-2" />
                    Add New Asset
                  </button>
                )}
              </div>
              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          <div className="row">
            <div className="col-xl-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0 me-2">
                        <span className="p-2 br-10 bg-pink-transparent border border-pink d-flex align-items-center justify-content-center">
                          <i className="ti ti-users-group text-pink fs-18" />
                        </span>
                      </div>
                      <div>
                        <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                          Total Assets
                        </p>
                        <h4>{assetSummary?.total_assets}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0 me-2">
                        <span className="p-2 br-10 bg-success-transparent border border-success d-flex align-items-center justify-content-center">
                          <i className="ti ti-user-share fs-18" />
                        </span>
                      </div>
                      <div>
                        <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                          Allocated Assets
                        </p>
                        <h4>{assetSummary?.allocated_assets}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0 me-2">
                        <span className="p-2 br-10 bg-success-transparent border border-success d-flex align-items-center justify-content-center">
                          <i className="ti ti-user-share fs-18" />
                        </span>
                      </div>
                      <div>
                        <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                          Return Assets
                        </p>
                        <h4>{assetSummary?.returned_assets}</h4>
                      </div>
                    </div>
                    {/* <span className="badge bg-transparent-primary text-primary d-inline-flex align-items-center fw-normal">
                                            <i className="ti ti-arrow-wave-right-down me-1" />
                                            +19.01%
                                        </span> */}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0 me-2">
                        <span className="p-2 br-10 bg-success-transparent border border-success d-flex align-items-center justify-content-center">
                          <i className="ti ti-user-share fs-18" />
                        </span>
                      </div>
                      <div>
                        <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                          Available Assets
                        </p>
                        <h4>{assetSummary?.available_assets}</h4>
                      </div>
                    </div>
                    {/* <span className="badge bg-transparent-primary text-primary d-inline-flex align-items-center fw-normal">
                                            <i className="ti ti-arrow-wave-right-down me-1" />
                                            +19.01%
                                        </span> */}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0 me-2">
                        <span className="p-2 br-10 bg-danger-transparent border border-danger d-flex align-items-center justify-content-center">
                          <i className="ti ti-user-pause fs-18" />
                        </span>
                      </div>
                      <div>
                        <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                          Damage Assets
                        </p>
                        <h4>{assetSummary?.damaged_assets}</h4>
                      </div>
                    </div>
                    {/* <span className="badge bg-transparent-dark text-dark d-inline-flex align-items-center fw-normal">
                                            <i className="ti ti-arrow-wave-right-down me-1" />
                                            +19.01%
                                        </span> */}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0 me-2">
                        <span className="p-2 br-10 bg-info-transparent border border-info d-flex align-items-center justify-content-center">
                          <i className="ti ti-user-plus fs-18" />
                        </span>
                      </div>
                      <div>
                        <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                          Lost Assets
                        </p>
                        <h4>{assetSummary?.lost_assets}</h4>
                      </div>
                    </div>
                    {/* <span className="badge bg-transparent-secondary text-dark d-inline-flex align-items-center fw-normal">
                                            <i className="ti ti-arrow-wave-right-down me-1" />
                                            +19.01%
                                        </span> */}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0 me-2">
                        <span className="p-2 br-10 bg-info-transparent border border-info d-flex align-items-center justify-content-center">
                          <i className="ti ti-user-plus fs-18" />
                        </span>
                      </div>
                      <div>
                        <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                          Repairing Assets
                        </p>
                        <h4>{assetSummary?.repair_assets}</h4>
                      </div>
                    </div>
                    {/* <span className="badge bg-transparent-secondary text-dark d-inline-flex align-items-center fw-normal">
                                            <i className="ti ti-arrow-wave-right-down me-1" />
                                            +19.01%
                                        </span> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Assets Lists */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Asset List</h5>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-3">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedStatus
                      ? selectedStatus.charAt(0).toUpperCase() +
                      selectedStatus.slice(1)
                      : "Select Status"}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("All")}
                        className="dropdown-item rounded-1"
                      >
                        All
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("active")}
                        className="dropdown-item rounded-1"
                      >
                        Active
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("inactive")}
                        className="dropdown-item rounded-1"
                      >
                        Inactive
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedSort
                      ? `Sort By : ${selectedSort}`
                      : "Sort By : Last 7 Days"}
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Recently Added")}
                        className="dropdown-item rounded-1"
                      >
                        Recently Added
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Ascending")}
                        className="dropdown-item rounded-1"
                      >
                        Ascending
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Descending")}
                        className="dropdown-item rounded-1"
                      >
                        Descending
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Last Month")}
                        className="dropdown-item rounded-1"
                      >
                        Last Month
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Last 7 Days")}
                        className="dropdown-item rounded-1"
                      >
                        Last 7 Days
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <Table
                key={tableKey}
                dataSource={filteredData}
                columns={columns}
                Selection={false}
              />
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}
      {/* Add Assets */}
      <Drawer
        title={modalType === "edit" ? "Edit Asset" : "Add Asset"}
        placement="right"
        width={700}
        onClose={handleCancel}
        visible={isModalVisible}
        style={{ marginTop: 50, paddingBottom: 50 }}
      >
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="modal-body pb-0">
            <div className="row">
              <div className="col-md-8">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Asset Name <span className="text-danger"> *</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={assetFormData.AssetName}
                        onChange={(e) => handleInputChange(e, "AssetName")}
                      />
                      {errors.AssetName && (
                        <div className="text-danger">{errors.AssetName}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Asset Category <span className="text-danger"> *</span>
                      </label>
                      <select
                        className="form-control"
                        value={assetFormData.asset_category_id}
                        onChange={(e) => handleInputChange(e, "asset_category_id")}
                      >
                        <option value="">Select Asset Category</option>
                        {activeAssetCategory
                          ?.sort((a, b) =>
                            a.category_name.localeCompare(b.category_name)
                          )
                          ?.map((cate) => (
                            <option
                              key={cate.asset_category_id}
                              value={cate.asset_category_id}
                            >
                              {cate.category_name}
                            </option>
                          ))}
                      </select>
                      {errors.asset_category_id && (
                        <div className="text-danger">{errors.asset_category_id}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-3">
                  <label className="form-label">
                    Upload Asset Image <span className="text-danger">*</span>
                  </label>
                  <div
                    style={{
                      padding: "12px",
                      border: "1px solid #f0f0f0",
                      textAlign: "center",
                      width: "100%",
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      className="position-relative mx-auto overflow-hidden rounded-circle border border-2 border-dashed d-flex align-items-center justify-content-center bg-light"
                      style={{ width: "100px", height: "100px", cursor: "pointer" }}
                      onClick={() => document.getElementById("assetFileInput")?.click()}
                    >
                      {assetFormData.file ? (
                        <>
                          <img src={assetFormData.file instanceof File ? URL.createObjectURL(assetFormData.file) : assetFormData.file?.data || "/images/no-image.png"}
                            alt="Asset Preview"
                            className="w-100 h-100 rounded-circle object-fit-cover"
                            onError={(e) => { e.currentTarget.src = "/images/no-image.png" }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger rounded-circle position-absolute"
                            style={{
                              top: "-6px",
                              right: "-6px",
                              width: "22px",
                              height: "22px",
                              padding: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12px",
                              zIndex: 5,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFile();
                            }}
                          >
                            <i className="ti ti-x" />
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="upload-hover position-absolute top-50 start-50 translate-middle text-center">
                            <div className="opacity-50 upload-icon">
                              <i className="ti ti-upload fs-4 text-muted"></i>
                            </div>
                          </div>
                          <div className="text-muted small">click here to upload</div>
                        </>
                      )}
                      {!assetFormData.file && (
                        <div
                          className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 d-flex align-items-center justify-content-center opacity-0 hover-opacity-100"
                          style={{ transition: "opacity 0.3s ease" }}
                        >
                          <i className="ti ti-upload fs-3 text-primary"></i>
                        </div>
                      )}
                    </div>
                    <input
                      id="assetFileInput"
                      type="file"
                      className="d-none"
                      name="file"
                      accept=".jpg,.jpeg,.png,.gif"
                      onChange={(e) => handleInputChange(e, "file")}
                    />
                    {errors.file && (
                      <div className="text-danger mt-2" style={{ fontSize: 12 }}>
                        {errors.file}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Asset Owner <span className="text-danger"> *</span>
                  </label>
                  <select
                    className="form-control"
                    value={assetFormData.OwnerEmpId}
                    onChange={(e) => handleInputChange(e, "OwnerEmpId")}
                  >
                    <option value="">Select Employee Name</option>
                    {[...activeAssetOwner]
                      .sort((a, b) => a.Name.localeCompare(b.Name))
                      .map((emp: any) => (
                        <option key={emp.EmployeeID} value={emp.EmployeeID}>
                          {emp.Name}
                        </option>
                      ))}
                  </select>
                  {errors.OwnerEmpId && (
                    <div className="text-danger">{errors.OwnerEmpId}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Asset Description
                  </label>
                  <input
                    className="form-control"
                    value={assetFormData.Description}
                    onChange={(e) => handleInputChange(e, "Description")}
                  />
                  {errors.Description && (
                    <div className="text-danger">{errors.Description}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Purchase Date <span className="text-danger"> *</span> </label>
                  <div className="input-icon-end position-relative">
                    <DatePicker
                      className="form-control datetimepicker"
                      format="DD-MM-YYYY"
                      value={assetFormData.PurchaseDate}
                      onChange={(date) =>
                        setAssetFormData({ ...assetFormData, PurchaseDate: date })
                      }
                      placeholder="DD-MM-YYYY"
                    />
                    {errors.PurchaseDate && (
                      <div className="text-danger">{errors.PurchaseDate}</div>
                    )}
                    <span className="input-icon-addon">
                      <i className="ti ti-calendar text-gray-7" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Purchase Price <span className="text-danger"> *</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={assetFormData.PurchasePrice}
                    onChange={(e) => handleInputChange(e, "PurchasePrice")}
                  />
                  {errors.PurchasePrice && (
                    <div className="text-danger">{errors.PurchasePrice}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Purchase From <span className="text-danger"> *</span> </label>
                  <select
                    className="form-control"
                    value={assetFormData.PurchaseFrom}
                    onChange={(e) => handleInputChange(e, "PurchaseFrom")}
                  >
                    <option value="">Select Vendor</option>

                    {filteredDataVendor.map((vendor: any) => (
                      <option key={vendor.client_id} value={vendor.client_id}>
                        {vendor.first_name} {vendor.last_name} ({vendor.company})
                      </option>
                    ))}
                  </select>

                  {errors.PurchaseFrom && (
                    <div className="text-danger">{errors.PurchaseFrom}</div>
                  )}

                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Purchase Order Number{" "}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={assetFormData.PurchaseOrderNumber}
                    onChange={(e) =>
                      handleInputChange(e, "PurchaseOrderNumber")
                    }
                  />
                  {errors.PurchaseOrderNumber && (
                    <div className="text-danger">{errors.PurchaseOrderNumber}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Purchase Order Bill</label>
                  <input
                    type="text"
                    className="form-control"
                    value={assetFormData.PurchaseOrderBill}
                    onChange={(e) => handleInputChange(e, "PurchaseOrderBill")}
                  />
                  {errors.PurchaseOrderBill && (
                    <div className="text-danger">{errors.PurchaseOrderBill}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Support Center <span className="text-danger"> *</span> </label>
                  <input
                    type="text"
                    className="form-control"
                    value={assetFormData.SupportCenter}
                    onChange={(e) => handleInputChange(e, "SupportCenter")}
                  />
                  {errors.SupportCenter && (
                    <div className="text-danger">{errors.SupportCenter}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Manufacturer <span className="text-danger"> *</span> </label>
                  <input
                    type="text"
                    className="form-control"
                    value={assetFormData.Manufacturer}
                    onChange={(e) => handleInputChange(e, "Manufacturer")}
                  />
                  {errors.Manufacturer && (
                    <div className="text-danger">{errors.Manufacturer}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Model <span className="text-danger"> *</span> </label>
                  <input
                    type="text"
                    className="form-control"
                    value={assetFormData.Model}
                    onChange={(e) => handleInputChange(e, "Model")}
                  />
                  {errors.Model && (
                    <div className="text-danger">{errors.Model}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Brand Name <span className="text-danger"> *</span> </label>
                  <input
                    type="text"
                    className="form-control"
                    value={assetFormData.BrandName}
                    onChange={(e) => handleInputChange(e, "BrandName")}
                  />
                  {errors.BrandName && (
                    <div className="text-danger">{errors.BrandName}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Production Year</label>{" "}
                  <span className="text-danger"> *</span>
                  <input
                    type="number"
                    className="form-control"
                    value={assetFormData.ProductionYear}
                    onChange={(e) => handleInputChange(e, "ProductionYear")}
                  />
                  {errors.ProductionYear && (
                    <div className="text-danger">{errors.ProductionYear}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Status <span className="text-danger"> *</span> </label>
                  <select
                    className="form-control"
                    value={assetFormData.status}
                    onChange={(e) => handleInputChange(e, "status")}
                  >
                    <option value="">Select Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {errors.status && (
                    <div className="text-danger">{errors.status}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Quantity <span className="text-danger"> *</span> </label>
                  <input
                    type="number"
                    className="form-control"
                    value={quantity}
                    onChange={handleQuantityChange}
                    min={1}
                    max={50}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-12">
            <div className="p-3 mb-4 border rounded bg-light">
              <h5 className="fw-bold text-primary">Asset Details</h5>

              <div className="table-responsive mt-3">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>S. No</th>
                      <th>
                        Asset Tag Number <span className="text-danger"> *</span>
                      </th>
                      <th>
                        Serial Number <span className="text-danger"> *</span>
                      </th>
                      <th>Warranty Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assetDetailsForm.assetDetails.map((asset, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>
                          <input
                            className="form-control"
                            type="text"
                            value={asset.AssetTagNumber}
                            onChange={(e) =>
                              handleAssetDetailsChange(
                                e,
                                index,
                                "AssetTagNumber"
                              )
                            }
                          />
                          {errors[`assetDetail_${index}`]?.AssetTagNumber && (
                            <div
                              className="text-danger"
                              style={{ fontSize: "0.675rem" }}
                            >
                              {errors[`assetDetail_${index}`]?.AssetTagNumber}
                            </div>
                          )}
                        </td>
                        <td>
                          <input
                            className="form-control"
                            type="text"
                            value={asset.SerialNumber}
                            onChange={(e) =>
                              handleAssetDetailsChange(e, index, "SerialNumber")
                            }
                          />
                          {errors[`assetDetail_${index}`]?.SerialNumber && (
                            <div
                              className="text-danger"
                              style={{ fontSize: "0.675rem" }}
                            >
                              {errors[`assetDetail_${index}`]?.SerialNumber}
                            </div>
                          )}
                        </td>
                        <td>
                          <input
                            className="form-control"
                            type="date"
                            value={asset.WarrantyDate.format("YYYY-MM-DD")}
                            onChange={(e) =>
                              handleAssetDetailsChange(e, index, "WarrantyDate")
                            }
                          />
                          {errors[`assetDetail_${index}`]?.WarrantyDate && (
                            <div
                              className="text-danger"
                              style={{ fontSize: "0.675rem" }}
                            >
                              {errors[`assetDetail_${index}`]?.WarrantyDate}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-light me-2"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              onClick={handleSubmit}
            >
              {modalType === "edit" ? "Update Asset" : "Add Asset"}
            </button>
          </div>
        </form>
      </Drawer>
      {/* /Add Assets */}

      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() =>
          selectedRowData?.assetId && handleDeleteAsset(selectedRowData.assetId)
        }
      />
      <AssetAllocationModel
        assetAllocationModal={assetAllocationModal}
        setAssetAllocationModal={setAssetAllocationModal}
        selectedRowData={selectedRowData}
        setTableKey={setTableKey}
      />
      <AssetLogDrawer
        open={isDrawerVisible}
        onClose={handleCloseDrawer}
        assetId={selectedAssetId}
        assetLogs={assetLogs}
        loading={loading}
      />
    </>
  );
};

export default Assets;
